import { Star, Quote } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "@/components/ui/carousel";
import { TESTIMONIALS, REVIEWS, CONTACT } from "@/data";

export default function Testimonials() {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-[var(--alt)] py-24 md:py-36 overflow-hidden">
      <div className="section-pad">
        <div className="max-w-2xl mb-14">
          <Overline>Client Reflections</Overline>
          <Reveal>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6">
              Words from those who found their way back.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CONTACT.googleReviews}
              target="_blank"
              rel="noreferrer"
              data-testid="google-rating-badge"
              className="inline-flex items-center gap-3 mt-7 group"
            >
              <span className="flex gap-1">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={18} className="fill-[var(--gold)] text-[var(--gold)]" />
                ))}
              </span>
              <span className="font-serif-display text-2xl text-[var(--ink)]">5.0</span>
              <span className="font-sans-ui text-sm text-[var(--muted-ink)] group-hover:text-[var(--ink)] transition-colors">rated on Google Reviews</span>
            </a>
          </Reveal>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="testimonials-carousel">
          <CarouselContent className="-ml-4">
            {TESTIMONIALS.map((t, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <div className="h-full bg-[var(--ivory)] border border-[rgba(200,149,54,0.3)] p-8 flex flex-col">
                  <Quote size={30} className="text-[var(--gold)] mb-5" strokeWidth={1.3} />
                  <p className="font-serif-display text-xl md:text-2xl leading-snug text-[var(--ink)] flex-1">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <div className="mt-7 pt-5 border-t border-[rgba(200,149,54,0.3)]">
                    <p className="font-sans-ui text-sm font-medium text-[var(--ink)]">{t.name}</p>
                    <p className="font-sans-ui text-xs text-[var(--muted-ink)] tracking-wide">{t.role}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex items-center gap-3 mt-10">
            <CarouselPrevious data-testid="testimonials-prev" className="static translate-y-0 border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--gold)] hover:text-[var(--noir)]" />
            <CarouselNext data-testid="testimonials-next" className="static translate-y-0 border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--gold)] hover:text-[var(--noir)]" />
          </div>
        </Carousel>

        {/* Google reviews trust block */}
        <div className="grid md:grid-cols-3 gap-6 mt-20">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="bg-[var(--panel)] border border-[rgba(200,149,54,0.25)] p-7 h-full">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: r.stars }).map((_, s) => (
                    <Star key={s} size={16} className="fill-[var(--gold)] text-[var(--gold)]" />
                  ))}
                </div>
                <p className="font-sans-ui text-sm text-[var(--muted-ink)] leading-relaxed font-light mb-4">&ldquo;{r.text}&rdquo;</p>
                <p className="font-sans-ui text-xs font-medium tracking-wide">{r.name}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-8">
            <a
              href={CONTACT.googleReviews}
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 font-sans-ui text-sm text-[var(--muted-ink)] hover:text-[var(--ink)] transition-colors"
            >
              <Star size={15} className="fill-[var(--gold)] text-[var(--gold)]" />
              Read more reviews on Google
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
